const searchDelay = 200;
const noResultsMsg = 'No results found.';
const defaultQueryParam = 'search';

const apiResponseKeyMissing
  = 'You need to specify "data-value-key" and "data-label-key" if the API returns objects or "data-simple-values" if the API returns a list of strings';
const arbitraryInputError = '"data-arbitrary-input" is only allowed when "data-simple-values" is activated.';

const findFormGroupElement = (element, selector) => {
  return element.closest('.form-group').querySelector(selector);
};

const insertAfter = (newNode, existing) => {
  existing.parentNode.insertBefore(newNode, existing.nextSibling);
};

const createOptionsContainer = (input) => {
  const container = document.createElement('div');
  container.classList.add('data-list');
  insertAfter(container, input);

  return container;
};

const createHiddenInput = (input, name) => {
  const hiddenField = document.createElement('input');
  const originalValue = input.value;
  const label = input.dataset.preselectLabel;
  hiddenField.type = 'hidden';
  hiddenField.name = name;
  insertAfter(hiddenField, input);
  hiddenField.setAttribute('value', originalValue);
  input.setAttribute('value', label);

  return hiddenField;
};

const createClearIcon = (parent, input) => {
  const icon = document.createElement('i');
  icon.classList.add('icon-close');
  parent.appendChild(icon);
  input.type = 'text';

  return icon;
};

const wrapElement = (input, classnames) => {
  const container = document.createElement('div');
  container.classList.add(...classnames);
  input.parentNode.insertBefore(container, input);
  container.appendChild(input);

  return container;
};

class Search {
  constructor(input) {
    const {
      arbitraryInput,
      labelKey,
      name,
      onSelectCallback,
      preselectLabel,
      queryParam,
      resultsKey,
      simpleValues,
      url,
      valueKey,
    } = input.dataset;

    this.container = input.parentElement;
    this.input = input;
    this.latestSearchValue = preselectLabel || input.value;
    this.inputWrapper = wrapElement(this.input, ['relative', 'grow']);
    this.optionsWrapper = findFormGroupElement(input, '[data-search-options]');
    this.optionsWrapper = createOptionsContainer(input);
    this.form = input.closest('form');
    this.name = name;
    this.url = url;
    this.queryParam = queryParam || defaultQueryParam;
    this.valueKey = valueKey;
    this.labelKey = labelKey;
    this.simpleValues = 'simpleValues' in input.dataset && simpleValues != 'false';
    this.resultsKey = resultsKey;
    this.searchTimeout = null;
    this.arbitraryInput = 'arbitraryInput' in input.dataset && arbitraryInput != 'false';
    this.onSelectCallback = onSelectCallback;

    if (!this.simpleValues && !this.valueKey && !this.labelKey) {
      throw new Error(apiResponseKeyMissing);
    }

    if (this.arbitraryInput && !this.simpleValues) {
      throw new Error(arbitraryInputError);
    }
    this.clearIcon = createClearIcon(this.inputWrapper, this.input);
    this.addClearListener();
    this.addKeyDownListener();
    this.addFocusListener();
    this.addKeyUpListener();
    this.addUnfocusEventListener();
  }

  addKeyDownListener() {
    this.input.addEventListener('keydown', () => {
      clearTimeout(this.searchTimeout);
    });
  }

  showOptions = () => {
    this.optionsWrapper.classList.add('active');
  };

  addFocusListener() {
    this.input.addEventListener('focusin', () => {
      this.showOptions();
    });
  }

  addClearListener() {
    this.clearIcon.addEventListener('click', () => {
      this.input.value = '';
      if (this.optionsWrapper) {
        this.optionsWrapper.classList.remove('active');
      }
      if (this.hiddeninput) {
        this.hiddeninput.value = '';
      }
    });
  }

  clearOptions() {
    this.optionsWrapper.replaceChildren();
  }

  setOptions(data) {
    let html;
    if (data.length) {
      html = data.map((item) => {
        let value, label;
        if (this.simpleValues) {
          value = item;
          label = item;
        } else {
          value = item.value;
          label = item.label;
        }
        const option = document.createElement('span');
        option.classList.add('data-item');
        option.dataset.value = value;
        option.innerText = label;
        if (
          (this.selection && this.selection.includes(item.value))
          || (this.hiddeninput && this.hiddeninput.value == item.value)
        ) {
          option.classList.add('selected');
        } else {
          option.addEventListener('click', () => this.handleClick(item));
        }

        return option;
      });
    } else {
      const msg = document.createElement('span');
      msg.classList.add('data-item');
      msg.innerText = noResultsMsg;
      html = [msg];
    }
    this.optionsWrapper.replaceChildren(...html);
  }

  addKeyUpListener() {
    this.input.addEventListener('keyup', (e) => {
      const value = e.currentTarget.value;
      if (value == this.latestSearchValue) {
        return;
      }
      clearTimeout(this.searchTimeout);
      if (this.hiddeninput && this.arbitraryInput) {
        this.hiddeninput.value = value;
      }
      if (!value) {
        this.optionsWrapper.innerHTML = '';
        if (this.hiddeninput) {
          this.hiddeninput.value = '';
        }

        return;
      }

      const setLoading = () => {
        const spinner = document.createElement('i');
        spinner.classList.add('icon-spinner-outline', 'rotate');
        const spinnerWrapper = document.createElement('span');
        spinnerWrapper.classList.add('inset-xs', 'flexrow', 'justify-center');
        spinnerWrapper.appendChild(spinner);
        this.optionsWrapper.replaceChildren(spinnerWrapper);
      };

      const execSearch = async (searchValue) => {
        this.latestSearchValue = searchValue;
        setLoading();
        this.showOptions();

        const requestUrl = `${this.url}?${this.queryParam}=${value}`;
        const response = await fetch(requestUrl);
        const results = await response.json();
        const data = this.resultsKey ? results[this.resultsKey] : results;

        let options;
        if (this.simpleValues) {
          options = data;
        } else {
          options = data.map((item) => {
            return { value: item[this.valueKey], label: item[this.labelKey] };
          });
        }
        this.setOptions(options);
      };
      this.searchTimeout = setTimeout(() => execSearch(value), searchDelay);
    });
  }

  addUnfocusEventListener() {
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target)) {
        this.optionsWrapper.classList.remove('active');
      }
    });
  }

  handleClick(item) {
    if (this.onSelectCallback && typeof window[this.onSelectCallback] === 'function') {
      window[this.onSelectCallback](item);
    }
  }
}

class SingleSearch extends Search {
  constructor(input) {
    super(input);
    this.hiddeninput = createHiddenInput(input, this.name);
    this.required = this.input.required;
    this.setSubmitListener();
  }

  setSubmitListener() {
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        if (this.required && !this.hiddeninput.value) {
          e.preventDefault();
          this.input.setCustomValidity('Please fill in this field.');
        }
      });
    }
  }

  handleClick(item) {
    this.input.setCustomValidity('');
    this.optionsWrapper.classList.remove('active');
    if (this.simpleValues) {
      this.hiddeninput.value = item;
      this.input.value = item;
    } else {
      this.hiddeninput.value = item.value;
      this.input.value = item.label;
    }
    this.clearOptions();
    super.handleClick(item);
  }
}

class MultiSearch extends Search {
  constructor(input) {
    super(input);
    this.selectionWrapper = findFormGroupElement(this.input, '[data-search-selection]');
    this.selection = [];
    if (this.arbitraryInput) {
      this.initArbitrarySearchInput();
    }
    this.initSelection();
  }

  createSelectedItem({ value, label }) {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.value = value;
    input.name = this.name;
    const tag = document.createElement('span');
    tag.setAttribute('data-selection-item', '');
    tag.innerText = label;
    const icon = document.createElement('i');
    icon.classList.add('icon-close');
    icon.addEventListener('click', () => this.removeSelected(tag, input.value));
    tag.appendChild(icon);
    tag.appendChild(input);
    if (!this.selection.includes(input.value)) {
      this.selectionWrapper.appendChild(tag);
      this.selection.push(input.value);
    }
  }

  initSelection() {
    const items = [...this.selectionWrapper.querySelectorAll('[data-selection-item]')];
    items.forEach((item) => {
      const { value, label } = item.dataset;
      if (this.simpleValues) {
        this.createSelectedItem({ value, label: value });
      } else {
        this.createSelectedItem({ value, label });
      }
      item.remove();
    });
  }

  removeSelected(item, value) {
    item.remove();
    const index = this.selection.indexOf(value);
    this.selection.splice(index, 1);
  }

  initArbitrarySearchInput() {
    const container = wrapElement(this.inputWrapper, ['relative', 'flexrow', 'flex-gap-xs', 'align-center']);

    const btn = document.createElement('button');
    btn.classList.add(...['is-icon', 'primary', 'small']);
    const icon = document.createElement('i');
    icon.classList.add('icon-add');
    btn.appendChild(icon);
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      const value = this.input.value;
      this.createSelectedItem({ value, label: value });
      this.input.value = '';
      this.clearOptions();
    });
    container.appendChild(btn);
  }

  handleClick(item) {
    const data = this.simpleValues ? { value: item, label: item } : item;
    this.createSelectedItem(data);
    this.optionsWrapper.classList.remove('active');
    this.clearOptions();
    super.handleClick(item);
  }
}

export const initLiveSearch = (targetEl, selector = '[data-search]') => {
  const target = targetEl || document;
  target.querySelectorAll(selector).forEach((el) => {
    if (el.dataset.multi) {
      new MultiSearch(el);
    } else {
      new SingleSearch(el);
    }
  });
};
